/**
 * Meter Mapping — aligned with production (evitals_production).
 * Fields: Model Number, Extension, Serial Number (16 alphanumeric), Gateway Serial Number (optional).
 */

export const METER_EXTENSION_OPTIONS = [
  { value: '1', label: 'User 1' },
  { value: '2', label: 'User 2' },
  { value: '3', label: 'User 3' },
  { value: '4', label: 'User 4' },
  { value: 'A', label: 'All' }
];

export const DEVICE_SERIAL_NUMBER_LENGTH = 16;
export const DEVICE_SERIAL_NUMBER_PATTERN = /^[A-Za-z0-9]{16}$/;

export const DEVICE_SERIAL_NUMBER_ERROR =
  'Device Serial Number must be exactly 16 alphanumeric characters';

/** Normalize stored/raw extension to option value (1–4, A). */
export function normalizeMeterExtensionValue(ext) {
  const raw = String(ext ?? '').trim();
  if (!raw) return '';

  const byValue = METER_EXTENSION_OPTIONS.find(
    (o) => String(o.value) === raw || String(o.value).toLowerCase() === raw.toLowerCase()
  );
  if (byValue) return byValue.value;

  const byLabel = METER_EXTENSION_OPTIONS.find(
    (o) => o.label.toLowerCase() === raw.toLowerCase()
  );
  if (byLabel) return byLabel.value;

  return '';
}

/** Display label for extension (e.g. "2" → "User 2", "A" → "All"). */
export function formatMeterExtensionLabel(ext) {
  const normalized = normalizeMeterExtensionValue(ext);
  if (normalized) {
    return METER_EXTENSION_OPTIONS.find((o) => o.value === normalized)?.label ?? normalized;
  }
  const raw = String(ext ?? '').trim();
  return raw || 'N/A';
}

/** Strip non-alphanumeric and cap at 16 chars while typing. */
export function normalizeDeviceSerialInput(value) {
  return String(value ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, DEVICE_SERIAL_NUMBER_LENGTH);
}

export function getDeviceSerialNumberError(serial, { required = true } = {}) {
  const trimmed = String(serial ?? '').trim();
  if (!trimmed) {
    return required ? 'Device Serial Number is required' : null;
  }
  if (!DEVICE_SERIAL_NUMBER_PATTERN.test(trimmed)) {
    return DEVICE_SERIAL_NUMBER_ERROR;
  }
  return null;
}

export function getMeterExtensionError(extension, { required = true } = {}) {
  const value = String(extension ?? '').trim();
  if (!value) {
    return required ? 'Extension is required' : null;
  }
  if (!METER_EXTENSION_OPTIONS.some((opt) => opt.value === value)) {
    return 'Please select a valid extension';
  }
  return null;
}

/** True when model, extension, and serial are all filled for a meter row. */
export function isCompleteMeterFormRow(meter) {
  const model = String(meter?.modelNumber ?? meter?.model_number ?? '').trim();
  const extension = String(meter?.extension ?? '').trim();
  const serial = String(meter?.serialNumber ?? meter?.serial_number ?? '').trim();
  if (!model || !extension || !serial) return false;
  if (getMeterExtensionError(extension)) return false;
  if (getDeviceSerialNumberError(serial)) return false;
  return true;
}

/** Returns first error message for a list of meter rows, or null if valid. */
export function validateMeterMappingList(meters) {
  if (!Array.isArray(meters)) return null;

  const rows = meters.filter((m) => {
    const model = String(m?.model_number ?? m?.modelNumber ?? '').trim();
    const serial = String(m?.serial_number ?? m?.serialNumber ?? '').trim();
    const ext = String(m?.extension ?? '').trim();
    return model || serial || ext;
  });

  const seenSerials = new Set();
  for (let i = 0; i < rows.length; i += 1) {
    const m = rows[i];
    const label = `Device #${i + 1}`;
    const model = String(m.model_number ?? m.modelNumber ?? '').trim();
    if (!model) return `${label}: Model number is required`;

    const extErr = getMeterExtensionError(m.extension ?? m.extension);
    if (extErr) return `${label}: ${extErr}`;

    const serialErr = getDeviceSerialNumberError(m.serial_number ?? m.serialNumber);
    if (serialErr) return `${label}: ${serialErr}`;

    const serialKey = String(m.serial_number ?? m.serialNumber ?? '').trim().toLowerCase();
    if (seenSerials.has(serialKey)) {
      return 'This serial number is already in the mapped devices list';
    }
    seenSerials.add(serialKey);
  }

  return null;
}

export function normalizeProductsDropdownResponse(res) {
  const raw = res?.data?.data ?? res?.data ?? res ?? [];
  return Array.isArray(raw) ? raw : [];
}

const METER_PRODUCT_VITAL_SUFFIX_BY_MODEL = {
  TD3261: 'BP + BG',
  TD2555: 'W',
};

function meterProductVitalSuffix(name, model) {
  const byModel = METER_PRODUCT_VITAL_SUFFIX_BY_MODEL[model.toUpperCase()];
  if (byModel) return byModel;

  const nameKey = name.replace(/['’]/g, '').toLowerCase();
  if (/\bd40g?\b/.test(nameKey)) return 'BP + BG';
  if (/\bscale\b/.test(nameKey)) return 'W';
  return '';
}

function withVitalSuffix(label, suffix) {
  if (!suffix) return label;
  const wrapped = `(${suffix})`;
  if (label.toLowerCase().includes(wrapped.toLowerCase())) return label;
  return `${label} ${wrapped}`;
}

/** Product dropdown / list label — Product Name only (model number stays in option value). */
export function formatMeterProductOptionLabel(product) {
  const name = String(product?.product_name ?? product?.name ?? '').trim();
  const model = String(product?.model_number ?? product?.modelNumber ?? '').trim();
  const label = name || model || 'Unknown product';
  return withVitalSuffix(label, meterProductVitalSuffix(name, model));
}

/** Resolve display label for a mapped device row from model_number + products list. */
export function resolveMeterProductDisplayLabel(modelNumber, products) {
  const model = String(modelNumber ?? '').trim();
  if (!model) return 'N/A';
  const list = Array.isArray(products) ? products : [];
  const match = list.find(
    (p) => String(p?.model_number ?? p?.modelNumber ?? '').trim() === model
  );
  if (match) return formatMeterProductOptionLabel(match);
  return model;
}
